import type { ValidAny } from "@/types";

/**
 * Amount from a raw extracted cell: "$2,460.94", "2460.94", "(1,234.56)" and
 * "1,234.56-" all parse; "" and non-numbers are null, never $0.00.
 *
 * One parser for every review-surface sum (claim-line totals in the expanded
 * panel, the YTD reconciliation check, the approve confirm's "$X paid"), so the
 * figures a reviewer compares across the screen are computed the same way.
 */
export const parseMoneyCell = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  let t = v.trim();
  if (t === "") return null;
  let negative = false;
  if (/^\(.*\)$/.test(t)) {
    negative = true;
    t = t.slice(1, -1);
  }
  if (t.endsWith("-")) {
    negative = true;
    t = t.slice(0, -1);
  }
  t = t.replace(/[$,\s]/g, "");
  if (t === "" || !/^-?\d*\.?\d+$/.test(t)) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return negative ? -Math.abs(n) : n;
};

/** Sum of the parseable amounts in `values`; unparseable cells contribute nothing. */
export const sumMoneyCells = (values: Iterable<ValidAny | unknown>): number => {
  let total = 0;
  for (const v of values) {
    const n = parseMoneyCell(v);
    if (n !== null) total += n;
  }
  return total;
};
